import { useEffect, useRef, useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePostHog } from "@/hooks/usePostHog";

interface DemoBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DemoBookingModal({
  open,
  onOpenChange,
}: DemoBookingModalProps) {
  const { posthog } = usePostHog();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const hasHandledSubmissionRef = useRef(false);
  const [formKey, setFormKey] = useState(0);

  // Increment key each time modal opens to force a fresh container
  useEffect(() => {
    if (open) {
      setFormKey((k) => k + 1);
    }
  }, [open]);

  const handleFormSubmitted = useCallback(() => {
    if (hasHandledSubmissionRef.current) return;
    hasHandledSubmissionRef.current = true;

    posthog?.capture("demo_form_submitted", {
      source: "hubspot_form",
      page: window.location.pathname,
      modal: "demo_booking_modal",
    });

    // Close the dialog so ChiliPiper's overlay is not blocked
    window.setTimeout(() => {
      onOpenChange(false);
    }, 250);
  }, [onOpenChange, posthog]);

  useEffect(() => {
    if (!open) return;

    hasHandledSubmissionRef.current = false;

    // Small delay to ensure the new container is rendered after key change
    const timerId = window.setTimeout(() => {
      const container = formContainerRef.current;
      if (!container) return;

      const existingScript = document.querySelector(
        'script[src="//js.hsforms.net/forms/embed/v2.js"]'
      );

      const createForm = () => {
        if ((window as any).hbspt && container) {
          container.innerHTML = "";
          (window as any).hbspt.forms.create({
            portalId: "99128",
            formId: "ea664590-de56-45ab-9719-4acf6b81ccc7",
            region: "na1",
            target: `#hubspot-demo-form-modal-${formKey}`,
            onFormSubmitted: handleFormSubmitted,
          });

          posthog?.capture("demo_form_loaded", {
            source: "hubspot_form",
            page: window.location.pathname,
            modal: "demo_booking_modal",
          });
        }
      };

      if (existingScript && (window as any).hbspt) {
        createForm();
      } else {
        const script = document.createElement("script");
        script.charset = "utf-8";
        script.type = "text/javascript";
        script.src = "//js.hsforms.net/forms/embed/v2.js";
        script.async = true;
        script.onload = () => createForm();
        document.head.appendChild(script);
      }
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [open, formKey, handleFormSubmitted, posthog]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] sm:max-h-[85vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle>Book a Demo</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-6 pt-4 min-h-0">
          <div
            id={`hubspot-demo-form-modal-${formKey}`}
            ref={formContainerRef}
            className="w-full min-h-[400px]"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
